import Game from '../../../Game';
import { ContextType } from '../../../State/Game/IContext';
import { IGameState } from '../../../State/Game/IGameState';
import BaseUISystem from '../BaseUISystem';
import { UIActions } from '../../../State/UI/UIActions';
import { StartAnimation } from '../../Animation/AnimationHelper';
import ElementComponent from '../../../Component/UI/Element';
import Renderable from '../../../Component/Renderable';
import { PlayerActions } from '../../../State/Player/PlayerActions';
import {
    GameActions,
    ResponseType,
    ResponseStatus,
} from '../../../State/Game/GameActions';
import {
    AdvertisementArea,
    AdvertisementType,
    RewardType,
} from '../../../Utility/SDK/Advertisement';
import { AnimationClipEvent } from '../../Animation/AnimationClip';
import {
    AUDIO_UI_BUTTON_CLOSE,
    AUDIO_UI_BUTTON_DEFAULT,
    AUDIO_UI_PRIZE_WHEEL_FANFARE,
} from '../../Audio';
import Interactable from '../../../Component/Interactable';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import {
    applyBounceButtonAnimation,
    applyDefaultButtonAnimation,
    removeBounceButtonAnimation,
} from '../UIHelper';
import BaseMenu from './BaseMenu';
import { StartAudio } from '../../Audio/AudioHelper';
import { AudioTrackType } from '../../Audio/AudioClip';
import {
    PRIZE_WHEEL_REWARDS,
    SpinWheelRewardType,
} from '../../../Constants/Reward';
import { PayloadType } from '../../../Utility/SDK/PayloadType';

const BTN_CLOSE = 'close_button';
const BTN_CLOSE_TEXT = 'close_button/close_button_text';
const BTN_SPIN = 'spin_button';
const BTN_CLAIM = 'claim_button';
const BTN_AD = 'ad_button';
const BTN_SHARE = 'share_button';
const PIN = 'wheel/pin';
const BRIGHT = 'wheel/bright';
const LIGHTS = 'wheel/lightbulb_container';

export default class WheelMenuHandler extends BaseMenu {
    private pin: GameObject;
    private bright: GameObject;
    private bulbs: GameObject[];
    private btnClose: GameObject;
    private btnCloseText: GameObject;
    private btnSpin: GameObject;
    private btnClaim: GameObject;
    private btnAd: GameObject;
    private btnShare: GameObject;
    private adWatched: boolean = false;
    private spinned: boolean = false;
    private angle: number;

    constructor(uiSystem: BaseUISystem) {
        super('wheelMenu', uiSystem);
        this.resources.set(
            'new_wheel',
            'resource/graphics/other/new_wheel.png'
        );
    }

    public handleStateChanged(state: IGameState): void {
        if (!this.adWatched) {
            if (
                state.context.response &&
                state.context.response.responseType ===
                    ResponseType.DISPLAY_ADVERTISEMENT
            ) {
                if (
                    state.context.response.data.payload.g_rewardType ===
                    RewardType.PRIZE_WHEEL_SPIN
                ) {
                    if (
                        state.context.response.status === ResponseStatus.SUCCESS
                    ) {
                        this.adWatched = true;
                        this.buttonPopOut(this.btnAd);
                        this.buttonPopIn(this.btnSpin);
                    } else {
                        this.adWatched = false;
                        this.buttonPopIn(this.btnAd);
                    }
                }
            }
        }

        if (this.spinned && state.playerState.spinWheelTask.length > 0) {
            this.setRewardAngle(state.playerState.spinWheelRewardType);
            this.startSpinWheel();
            this.spinned = false;
        }
    }

    protected onCreate() {
        this.pin = this.element.find(PIN);
        this.bright = this.element.find(BRIGHT);
        this.bulbs = this.element.find(LIGHTS).children;
        this.btnClose = this.element.find(BTN_CLOSE);
        this.btnCloseText = this.element.find(BTN_CLOSE_TEXT);
        this.btnSpin = this.element.find(BTN_SPIN);
        this.btnClaim = this.element.find(BTN_CLAIM);
        this.btnAd = this.element.find(BTN_AD);
        this.btnShare = this.element.find(BTN_SHARE);

        this.btnClose.on(GameObjectEvent.SELECTED, () => {
            this.btnClose.getComponent(Interactable).enabled = false;
            this.system.state.dispatch(UIActions.hidePopup(this.name));
        });
        applyDefaultButtonAnimation(this.btnClose, AUDIO_UI_BUTTON_CLOSE);

        this.btnSpin.on(GameObjectEvent.SELECTED, () => {
            this.btnSpin.getComponent(Interactable).enabled = false;
            this.btnClose.getComponent(Interactable).enabled = false;
            this.spin();
        });
        applyBounceButtonAnimation(this.btnSpin, AUDIO_UI_BUTTON_DEFAULT);

        this.btnClaim.on(GameObjectEvent.SELECTED, () => {
            this.btnClaim.getComponent(Interactable).enabled = false;
            this.claim();
        });
        applyBounceButtonAnimation(this.btnClaim, AUDIO_UI_BUTTON_DEFAULT);

        this.btnAd.on(GameObjectEvent.SELECTED, () => {
            this.btnAd.getComponent(Interactable).enabled = false;
            this.watchAd();
        });
        applyBounceButtonAnimation(this.btnAd, AUDIO_UI_BUTTON_DEFAULT);

        this.btnShare.on(GameObjectEvent.SELECTED, () => {
            Game.state.dispatch(
                GameActions.setContext(ContextType.SHARE_SCREENSHOT, {
                    message: Game.locale.getText('ShareWheel'),
                    payloadData: PayloadType.SHARE_PRIZEWHEEL,
                })
            );
        });
        applyDefaultButtonAnimation(this.btnShare);
    }

    protected onShow(): void {
        this.adWatched = false;

        // Always starts at the "first" slice.
        this.pin.getComponent(ElementComponent).rotation = 0;
        this.bright.getComponent(ElementComponent).rotation = -Math.PI / 4;

        this.btnClose.getComponent(Interactable).enabled = true;
        this.btnCloseText.getComponent(Renderable).visible = true;
        this.btnCloseText.getComponent(Renderable).alpha = 1;
        this.btnCloseText.getComponent(ElementComponent).scale.set(1, 1);

        this.btnSpin.getComponent(Renderable).visible = false;
        this.btnClaim.getComponent(Renderable).visible = false;
        this.btnAd.getComponent(Renderable).visible = false;

        if (this.system.state.getState().playerState.wheelSpins > 0) {
            this.buttonPopIn(this.btnSpin);
        } else if (Game.state.getState().platform.adsRewarded) {
            this.buttonPopIn(this.btnAd);
        }
    }

    protected onHide(params?: any): void {
        this.btnClose.getComponent(Interactable).enabled = false;
        this.btnSpin.getComponent(Interactable).enabled = false;
        this.btnClaim.getComponent(Interactable).enabled = false;
        this.btnAd.getComponent(Interactable).enabled = false;
    }

    protected onShown(): void {
        StartAnimation(this.element, 'WheelLightBulbs', {
            bulbs: this.bulbs,
        });
    }

    private watchAd(): void {
        this.buttonPopOut(this.btnAd);

        this.system.state.dispatch(
            GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
                placement: {
                    id: 0 /*changed from Game.config.adRewardedID*/,
                    type: AdvertisementType.REWARDED,
                },
                payload: {
                    g_gameArea: AdvertisementArea.PRIZE_WHEEL,
                    g_rewardType: RewardType.PRIZE_WHEEL_SPIN,
                },
            })
        );
    }

    private spin(): void {
        this.spinned = true;
        this.system.state.dispatch(PlayerActions.spinWheel());
    }

    private claim(): void {
        this.buttonPopOut(this.btnClaim);
        this.system.state.dispatch(UIActions.hidePopup(this.name));
        this.system.state.dispatch(PlayerActions.claimReward());
    }

    private startSpinWheel(): void {
        this.buttonPopOut(this.btnSpin);
        this.buttonPopOut(this.btnClose);

        StartAnimation(this.element, 'WheelSpin', {
            scene: this.system.scene,
            pin: this.pin,
            bright: this.bright,
            bulbs: this.bulbs,
            angle: this.angle,
        }).once(AnimationClipEvent.COMPLETE, this.stopSpinWheel, this);
    }

    private stopSpinWheel(): void {
        StartAnimation(this.element, 'WheelSpinComplete', {
            scene: this.system.scene,
            pin: this.pin,
            bright: this.bright,
            bulbs: this.bulbs,
            angle: this.angle,
        });

        StartAudio(
            this.system.scene,
            AUDIO_UI_PRIZE_WHEEL_FANFARE,
            AudioTrackType.SFX
        );

        this.buttonPopIn(this.btnClaim);
        this.btnShare.getComponent(
            Renderable
        ).visible = Game.sdk.driver.hasShareableMomentsSupport();
    }

    private buttonPopIn(button: GameObject): void {
        button.getComponent(Interactable).enabled = false;
        StartAnimation(button, 'UIPopIn', { scale: [1.5, 1.5] }).once(
            AnimationClipEvent.COMPLETE,
            () => {
                button.getComponent(Interactable).enabled = true;
                applyBounceButtonAnimation(
                    this.btnClaim,
                    AUDIO_UI_BUTTON_DEFAULT
                );
            }
        );
    }

    private buttonPopOut(button: GameObject): void {
        button.getComponent(Interactable).enabled = false;
        removeBounceButtonAnimation(button);
        StartAnimation(button, 'UIPopOut', {});
    }

    private setRewardAngle(rewardType: SpinWheelRewardType): void {
        this.angle = PRIZE_WHEEL_REWARDS.find(r => r.type === rewardType).angle;
    }
}
